import {
  useAssetClassList,
  useAssetTypeList,
  useSearchAssetClassList,
  useTypeDimensionList,
} from "@features/ax/settings/assetClassification/hooks";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Collapsible, CollapsibleContent } from "@shared/ui/collapsible";
import { Input } from "@shared/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { ChevronDown, ChevronUp, Plus, Settings2 } from "lucide-react";
import { useState } from "react";
import ConditionAssessmentModal from "./conditionalAssessmentModal";
import { ManageDimensions } from "./create";

interface AssetClassItemProps {
  assetClass: any;
  assetTypes: any[];
  onManageDimensions: (assetClass: any, assetType: any) => void;
  onToggleExpand: (assetClassCode: string, isOpen: boolean) => void;
  isExpanded: boolean;
  onAddConditions: (assetClass: any, assetType: any) => void;
  setShowManageDimensions: (show: boolean) => void;
  setShowAdd: (show: boolean) => void;
}

const AssetClassItem: React.FC<AssetClassItemProps> = ({
  assetClass,
  assetTypes = [],
  onManageDimensions,
  onToggleExpand,
  isExpanded,
  onAddConditions,
  setShowManageDimensions,
  setShowAdd,
}) => {
  const handleToggle = () => {
    onToggleExpand(assetClass.code, !isExpanded);
  };

  return (
    <div className="rounded-md border mb-3 transition-colors overflow-hidden">
      <div className="flex items-center justify-between p-4 hover:bg-muted/30">
        <div className="flex-1 cursor-pointer" onClick={handleToggle}>
          <div className="flex items-center gap-3">
            <span className="text-lg font-semibold text-blue-600">
              {assetClass.name}
            </span>
            <Badge variant="secondary">
              {assetClass.childCount || 0} types
            </Badge>
          </div>
          {assetClass.description && (
            <div className="mt-2">
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md border-l-4 border-blue-200">
                <span className="font-medium text-gray-800">Description: </span>
                {assetClass.description}
              </p>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm" onClick={handleToggle}>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
      <Collapsible open={isExpanded} onOpenChange={handleToggle}>
        <CollapsibleContent>
          <div className="bg-muted/20 p-4 border-t">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-sm font-medium">
                Asset Type,Dimensions & Conditions
              </h4>
            </div>
            {assetTypes.length > 0 ? (
              <div className="space-y-3">
                {assetTypes.map((assetType) => (
                  <div
                    key={assetType.assetTypeCode}
                    className="border rounded-lg p-4 bg-background"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex flex-col gap-2 flex-1">
                        <div className="flex items-center gap-3">
                          <h4 className="font-medium text-gray-900">
                            {assetType.assetType}
                          </h4>
                          <Badge variant="outline">
                            {assetType.assetDimensions?.length || 0} dimensions
                          </Badge>
                        </div>
                        {assetType.description && (
                          <div className="w-full">
                            <p className="text-sm text-gray-600 bg-white p-3 rounded-md border-l-4 border-green-200">
                              <span className="font-medium text-gray-800">
                                Description:{" "}
                              </span>
                              {assetType.description}
                            </p>
                          </div>
                        )}

                        {/* Dimensions Preview */}
                        {assetType.assetDimensions &&
                          assetType.assetDimensions.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {assetType.assetDimensions
                                .slice(0, 3)
                                .map((dim: any) => (
                                  <Badge
                                    key={dim.id}
                                    variant="secondary"
                                    className="text-xs"
                                  >
                                    {dim.name}
                                    {dim.unit && ` (${dim.unit})`}
                                  </Badge>
                                ))}

                              {assetType.assetDimensions.length > 3 && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div>
                                        <Badge
                                          variant="outline"
                                          className="text-xs cursor-pointer"
                                        >
                                          +
                                          {assetType.assetDimensions.length - 3}{" "}
                                          more
                                        </Badge>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs">
                                      <div className="flex flex-wrap gap-1">
                                        {assetType.assetDimensions
                                          .slice(3)
                                          .map((dim: any) => (
                                            <Badge
                                              key={dim.id}
                                              variant="secondary"
                                              className="text-xs"
                                            >
                                              {dim.name}
                                              {dim.unit && ` (${dim.unit})`}
                                            </Badge>
                                          ))}
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          )}

                        {(!assetType.assetDimensions ||
                          assetType.assetDimensions.length === 0) && (
                          <Badge variant="secondary" className="text-xs w-fit">
                            No dimensions defined
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            onAddConditions(assetClass, assetType);
                            setShowAdd(true);
                          }}
                          className="flex items-center gap-2"
                        >
                          <Plus className="w-4 h-4" />
                          Add Conditions
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            onManageDimensions(assetClass, assetType)
                          }
                          className="flex items-center gap-2"
                        >
                          <Settings2 className="w-4 h-4" />
                          Manage Dimensions
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No asset types defined
              </p>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export const DimensionsList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAssetClass, setSelectedAssetClass] = useState<any>(null);
  const [selectedAssetType, setSelectedAssetType] = useState<any>(null);
  const [showManageDimensions, setShowManageDimensions] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  // SIMPLIFIED: Only track which single asset class is expanded
  const [expandedAssetClassCode, setExpandedAssetClassCode] = useState<
    string | null
  >(null);

  // Fetch asset classes using the same hooks
  const { data: assetClassList, isLoading: isLoadingAssetClasses } =
    useAssetClassList();

  // Use search hook like the original component
  const { data: assetSearchClassList, isLoading: isLoadingSearchAssetClasses } =
    useSearchAssetClassList(searchTerm);

  // Conditionally fetch asset types only when an asset class is expanded
  const { data: assetTypeList, isLoading: isLoadingAssetTypes } =
    useAssetTypeList(expandedAssetClassCode || undefined);

  // Get dimensions for the expanded asset types
  const { data: typeDimensionsData } = useTypeDimensionList(
    expandedAssetClassCode
      ? { asset_class_code: expandedAssetClassCode, page: 1, limit: 100 }
      : undefined
  );

  console.log("Expanded Asset Class Code:", typeDimensionsData);

  const handleAssetClassToggle = (assetClassCode: string, isOpen: boolean) => {
    if (isOpen) {
      setExpandedAssetClassCode(assetClassCode);
    } else {
      setExpandedAssetClassCode(null);
    }
  };

  const handleManageDimensions = (assetClass: any, assetType: any) => {
    setSelectedAssetClass(assetClass);
    setSelectedAssetType(assetType);
    setShowManageDimensions(true);
  };

  const handleAddConditions = (assetClass: any, assetType: any) => {
    setSelectedAssetClass(assetClass);
    setSelectedAssetType(assetType);
    console.log("Selected Asset Type for Conditions:", assetType);
    setShowAdd(true);
  };

  if (isLoadingAssetClasses) {
    return (
      <div className="space-y-4">
        <div className="text-center py-6">
          <p className="text-sm text-muted-foreground">
            Loading asset classes...
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Search Input */}
      <div className="flex justify-between gap-4 mt-5">
        <div>
          <Input
            id="search"
            placeholder="Search asset classes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-96"
          />
        </div>
      </div>

      {/* Asset Classification Hierarchy */}
      <Card className="mt-5">
        <CardContent>
          {(searchTerm.length > 0 ? assetSearchClassList : assetClassList)
            ?.length > 0 ? (
            <div>
              {(searchTerm.length > 0
                ? assetSearchClassList
                : assetClassList
              ).map((assetClass) => (
                <AssetClassItem
                  key={assetClass.code}
                  assetClass={assetClass}
                  assetTypes={typeDimensionsData?.result || []}
                  onManageDimensions={handleManageDimensions}
                  onToggleExpand={handleAssetClassToggle}
                  isExpanded={expandedAssetClassCode === assetClass.code}
                  onAddConditions={handleAddConditions}
                  setShowManageDimensions={setShowManageDimensions}
                  setShowAdd={setShowAdd}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-6 border rounded-md bg-muted/30">
              <Settings2 className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
              <h3 className="font-medium mb-1">No Asset Classes Found</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
                {searchTerm
                  ? `No asset classes found matching "${searchTerm}". Try adjusting your search.`
                  : "No asset classes available for dimension management."}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manage Dimensions Dialog */}
      <ManageDimensions
        editingDimensions={selectedAssetType}
        showManageDimensions={showManageDimensions}
        onClose={() => {
          setShowManageDimensions(false);
          setSelectedAssetType(null);
          setSelectedAssetClass(null);
        }}
      />
      <ConditionAssessmentModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        assetCode={selectedAssetType?.assetTypeCode}
        assetName={selectedAssetType?.assetType}
        assetClassCode={selectedAssetClass?.code}
        title="Asset Condition Assessment"
      />
    </>
  );
};
